import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useAssignments } from '@/store/assignment';
import { useRoles } from '@/store/role';
import { useTenant } from '@/store/tenant';
import { Shield, Users, Search, Plus, Trash2, Key } from 'lucide-react';
import type { Tenant } from '@/types';

interface AssignmentTabProps {
    tenant: Tenant;
}

export const AssignmentsTab: React.FC<AssignmentTabProps> = ({ tenant }) => {
    const listAssignments = useAssignments((state) => state.list);
    const assignmentsMap = useAssignments((state) => state.assignments);
    const createAssignment = useAssignments((state) => state.create);
    const deleteAssignment = useAssignments((state) => state.delete);
    const isAssigning = useAssignments((state) => state.isLoading);

    const listRoles = useRoles((state) => state.list);
    const roles = useRoles((state) => state.roles);

    const tenantStore = useTenant(tenant.id);
    const tenantUsers = tenantStore.users.data;
    const tenantGroups = tenantStore.groups.data;
    const tenantApps = tenantStore.apps.data; // array of app IDs

    // Default scope is the tenant itself
    const platformScopes = [`/platform/tenants/${tenant.id}`];
    // Add app scopes for enabled apps
    const appScopes = tenantApps.map(appId => `/platform/tenants/${tenant.id}/apps/${appId}`);

    const allScopes = [...platformScopes, ...appScopes];

    const [selectedScope] = useState(platformScopes[0]);

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [assignmentPrincipalType, setAssignmentPrincipalType] = useState<'user' | 'group'>('user');
    const [selectedPrincipalId, setSelectedPrincipalId] = useState('');
    const [selectedRoleId, setSelectedRoleId] = useState('');

    useEffect(() => {
        // Fetch roles available for these scopes
        listRoles();
        // Fetch assignments for all scopes
        allScopes.forEach(s => listAssignments(s));
    }, [tenant.id]);

    const activeAssignments = assignmentsMap[selectedScope] || [];

    const availableRoles = roles.filter(r => {
        if (selectedScope === platformScopes[0]) {
            return r.applicable_scopes?.includes('Tenant') || r.applicable_scopes?.includes('Any');
        } else {
            // It's an app scope
            return r.applicable_scopes?.includes('TenantApplication') || r.applicable_scopes?.includes('Any');
        }
    });

    const handleAssign = async () => {
        if (!selectedPrincipalId || !selectedRoleId) return;
        try {
            await createAssignment({
                principal_id: selectedPrincipalId,
                principal_type: assignmentPrincipalType,
                role_id: selectedRoleId,
                scope_id: selectedScope
            });
            toast.success('Assignment created');
            setIsAddModalOpen(false);
            listAssignments(selectedScope);
        } catch (err) {
            toast.error('Failed to create assignment');
        }
    };

    const handleRemove = async (assignmentId: string, scopeId: string, principalId: string) => {
        try {
            await deleteAssignment(assignmentId, scopeId, principalId);
            toast.success('Assignment removed');
            listAssignments(selectedScope);
        } catch (err) {
            toast.error('Failed to remove assignment');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <div>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">Role Assignments</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Manage what users and groups can do within this specific tenant scope.</p>
                </div>

                <div className="flex gap-3 items-center">
                    <Button
                        onClick={() => {
                            setSelectedPrincipalId('');
                            setSelectedRoleId('');
                            setIsAddModalOpen(true);
                        }}
                        className="bg-orange-500 hover:bg-orange-600 text-white shadow-md border-0 gap-2 px-4 rounded-lg"
                    >
                        <Plus className="w-4 h-4" /> Assign Role
                    </Button>
                </div>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200/60 dark:border-slate-800/60 rounded-xl shadow-[0_2px_10px_-3px_rgba(6,81,237,0.05)] overflow-hidden">
                {activeAssignments.length === 0 ? (

                    <div className="py-12 text-center">
                        <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Key className="w-8 h-8 text-slate-400" />
                        </div>
                        <p className="text-slate-500 dark:text-slate-400">No roles assigned at this scope.</p>
                        <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                            Assign roles to users or groups to grant access
                        </p>
                    </div>
                ) : (
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-slate-50/80 dark:bg-slate-900/50 text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b border-slate-100/60 dark:border-slate-800/60">
                                <th className="px-6 py-3">Principal</th>
                                <th className="px-6 py-3">Type</th>
                                <th className="px-6 py-3">Assigned Role</th>
                                <th className="px-6 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100/60 dark:divide-slate-800/60">
                            {activeAssignments.map((assignment) => {
                                const isUser = assignment.principal_type === 'user';
                                const principalName = isUser
                                    ? tenantUsers.find(u => u.id === assignment.principal_id)?.name || assignment.principal_id
                                    : tenantGroups.find(g => g.id === assignment.principal_id)?.name || assignment.principal_id;

                                const roleName = roles.find(r => r.id === assignment.role_id)?.name || assignment.role_id;

                                return (
                                    <tr key={`${assignment.principal_id}-${assignment.role_id}`} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all">
                                        <td className="px-6 py-3 font-bold text-[13px] text-slate-900 dark:text-white">
                                            {principalName}
                                        </td>
                                        <td className="px-6 py-3">
                                            <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 w-max ${isUser ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400' : 'bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400'}`}>
                                                {isUser ? <Users className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
                                                {assignment.principal_type}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3 text-[13px] font-medium text-slate-700 dark:text-slate-300">
                                            {roleName}
                                        </td>
                                        <td className="px-6 py-3 text-right">
                                            <button
                                                className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 rounded-md transition-colors"
                                                onClick={() => handleRemove(assignment.id, assignment.scope_id, assignment.principal_id)}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {isAddModalOpen && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex items-center justify-center z-50 p-4 transition-all animate-in fade-in">
                    <Card className="max-w-md w-full shadow-2xl scale-in-center overflow-hidden" title="Assign Role" subtitle="Grant a user or group a role for this scope.">
                        <div className="space-y-6">

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Principal Type</label>
                                <div className="flex gap-2">
                                    <Button
                                        variant={assignmentPrincipalType === 'user' ? 'primary' : 'outline'}
                                        className={assignmentPrincipalType === 'user' ? 'bg-[#1e293b] text-white' : ''}
                                        onClick={() => setAssignmentPrincipalType('user')}
                                    >
                                        User
                                    </Button>
                                    <Button
                                        variant={assignmentPrincipalType === 'group' ? 'primary' : 'outline'}
                                        className={assignmentPrincipalType === 'group' ? 'bg-[#1e293b] text-white' : ''}
                                        onClick={() => setAssignmentPrincipalType('group')}
                                    >
                                        Group
                                    </Button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Select {assignmentPrincipalType === 'user' ? 'User' : 'Group'}</label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <select
                                        className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-4 focus:ring-(--primary)/10 focus:border-(--primary) transition-all font-medium text-slate-900 dark:text-white w-full outline-none appearance-none"
                                        value={selectedPrincipalId}
                                        onChange={(e) => setSelectedPrincipalId(e.target.value)}
                                    >
                                        <option value="" disabled>Search {assignmentPrincipalType}s...</option>
                                        {assignmentPrincipalType === 'user' && tenantUsers.map(u => (
                                            <option key={u.id} value={u.id}>{u.email || u.name || u.id}</option>
                                        ))}
                                        {assignmentPrincipalType === 'group' && tenantGroups.map(g => (
                                            <option key={g.id} value={g.id}>{g.name || g.id}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Select Role</label>
                                <select
                                    className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-4 focus:ring-(--primary)/10 focus:border-(--primary) transition-all font-medium text-slate-900 dark:text-white w-full outline-none appearance-none"
                                    value={selectedRoleId}
                                    onChange={(e) => setSelectedRoleId(e.target.value)}
                                >
                                    <option value="" disabled>Select a role...</option>
                                    {availableRoles.map(r => (
                                        <option key={r.id} value={r.id}>{r.name} ({r.applicable_scopes?.join(', ') || 'Any'})</option>
                                    ))}
                                </select>
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t border-slate-100 dark:border-slate-800">
                                <Button variant="ghost" onClick={() => setIsAddModalOpen(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    disabled={!selectedPrincipalId || !selectedRoleId || isAssigning}
                                    onClick={handleAssign}
                                >
                                    Assign
                                </Button>
                            </div>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};
